import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const RED = "#E6152A";
const BLACK = "#0F0F0F";
const FAMILY = "'Big Shoulders Display', sans-serif";

/**
 * Renders "PREMIÈRE" with:
 *  - a custom, thin, discreet grave accent on the È (overriding the chunky built-in one)
 *  - per-letter vertical offsets for a slightly irregular / dynamic feel
 *
 * `offsets` — vertical translation per letter (0..7 = P R E M I È R E)
 * `weight` — font weight applied to the letters
 * `size`   — root font-size of the word
 * `spacing`— letter-spacing (em)
 * `accentWidth` — width of the custom accent stroke (px)
 */
const Premiere = ({ offsets, weight, size, spacing, accentWidth }) => {
  const letters = ["P", "R", "E", "M", "I", "E", "R", "E"]; // E at index 5 gets the accent
  return (
    <div
      style={{
        fontFamily: FAMILY,
        fontWeight: weight,
        color: BLACK,
        fontSize: size,
        letterSpacing: `${spacing}em`,
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "baseline",
      }}
    >
      {letters.map((ch, i) => {
        const t = offsets[i] || 0;
        if (i === 5) {
          // È with custom fine grave accent above the E
          return (
            <span
              key={i}
              style={{
                display: "inline-block",
                position: "relative",
                transform: `translateY(${t}px)`,
              }}
            >
              E
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "0.14em",
                  top: "-0.22em",
                  width: "0.28em",
                  height: `${accentWidth}px`,
                  background: BLACK,
                  transform: "rotate(22deg)",
                  transformOrigin: "left top",
                  borderRadius: "1px",
                }}
              />
            </span>
          );
        }
        return (
          <span
            key={i}
            style={{ display: "inline-block", transform: `translateY(${t}px)` }}
          >
            {ch}
          </span>
        );
      })}
    </div>
  );
};

const variants = [
  {
    key: "D2a",
    label: "D2a — PREMIÈRE 700, décalages fins",
    desc: "Graisse 700 (plus de présence qu'en 600 sans écraser ROUGE). Décalages verticaux subtils, jusqu'à ±2 px, presque imperceptibles au premier regard mais qui vibrent en lecture attentive. Accent grave refait en trait fin (2 px), incliné à 22°, discrètement posé.",
    render: (rougeSize) => (
      <div className="flex flex-col items-start leading-[0.85]">
        <span style={{ fontFamily: FAMILY, fontWeight: 900, color: RED, fontSize: rougeSize, letterSpacing: "0.005em" }}>
          ROUGE
        </span>
        <div style={{ marginTop: "0.25em" }}>
          <Premiere
            offsets={[-1, 0, 1, -1, 0, 1, -1, 0]}
            weight={700}
            size={`calc(${rougeSize} * 0.36)`}
            spacing={0.32}
            accentWidth={2}
          />
        </div>
      </div>
    ),
  },
  {
    key: "D2b",
    label: "D2b — PREMIÈRE 800, décalages plus marqués",
    desc: "Graisse 800 (plus de matière, s'imposant tout en restant sous ROUGE). Décalages verticaux jusqu'à ±3 px, effet plus vivant et affirmé, façon presse rock/culturelle. Accent grave encore plus fin (1.5 px) et raccourci, quasi invisible mais présent pour la justesse orthographique.",
    render: (rougeSize) => (
      <div className="flex flex-col items-start leading-[0.85]">
        <span style={{ fontFamily: FAMILY, fontWeight: 900, color: RED, fontSize: rougeSize, letterSpacing: "0.005em" }}>
          ROUGE
        </span>
        <div style={{ marginTop: "0.2em" }}>
          <Premiere
            offsets={[2, -2, 1, -3, 2, -1, 3, -2]}
            weight={800}
            size={`calc(${rougeSize} * 0.38)`}
            spacing={0.22}
            accentWidth={1.5}
          />
        </div>
      </div>
    ),
  },
];

export const LogoCompare = () => (
  <div className="min-h-screen bg-white">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[var(--red)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Retour à l'accueil
      </Link>

      <div className="mt-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">
          Reprise de D2 · ajustements
        </div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">
          Deux versions de D2 retravaillées
        </h1>
        <p className="text-neutral-600 mt-3 max-w-2xl">
          « PREMIÈRE » gagne en présence, ses lettres sont légèrement décalées à la
          verticale pour un effet dynamique, et l'accent grave a été redessiné en trait
          fin plutôt que reprendre le chunky par défaut de la police.
        </p>
      </div>

      <div className="mt-12 space-y-10">
        {variants.map((v) => (
          <div
            key={v.key}
            data-testid={`d2-${v.key}`}
            className="rounded-md border border-[var(--line)] bg-white overflow-hidden"
          >
            <div className="bg-white p-12 md:p-16 flex items-center justify-center min-h-[440px] border-b border-[var(--line)]">
              {v.render("170px")}
            </div>
            <div className="bg-[var(--bg-cream)] px-8 py-6 flex flex-wrap items-center gap-10 border-b border-[var(--line)]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 shrink-0">
                Petits formats
              </div>
              <div className="flex items-end gap-10">
                <div className="flex flex-col items-center gap-2">
                  {v.render("58px")}
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500">58 px</span>
                </div>
                <div className="flex flex-col items-center gap-2">
                  {v.render("32px")}
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500">32 px</span>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--red)]">
                Variante {v.key}
              </div>
              <h3 className="font-display text-2xl mt-1">
                {v.label.split("—")[1]?.trim() || v.label}
              </h3>
              <p className="text-neutral-600 mt-2 leading-relaxed max-w-3xl">{v.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 rounded-md border border-dashed border-[var(--line-strong)] bg-[var(--bg-cream)] p-6 text-sm text-neutral-700">
        <div className="font-display text-lg mb-1">Comment choisir ?</div>
        Dites-moi <span className="font-semibold text-[var(--red)]">« Je choisis D2a »</span>
        ou <span className="font-semibold text-[var(--red)]">« D2b »</span>, ou proposez un
        ajustement (« D2a mais décalages un peu plus visibles », « D2b avec accent
        un poil plus long »). J'installe ensuite dans le header et le footer.
      </div>
    </div>
  </div>
);
